import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useMemo, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { SearchableSelect } from '../../../components/SearchableSelect';
import { Button, DateRange, ErrorNote, Field, Input, Modal, Textarea } from '../../../components/ui';
import { filterByQuery } from '../../../lib/listSearch';
import { api } from '../../../lib/api';
import type { Features } from '../../../lib/auth';
import { titleCase } from '../../../lib/format';
import type { VenuePrinterDefaults } from '../../../lib/print/shop-printer';

// ------------------------------------------------------------------ shared server shapes
export interface AppSettings {
  features: Features;
  business: { name: string; address?: string | null; timezone: string; currency: 'INR'; supportPhone?: string | null };
  payments: { modes: string[]; topupMinPaise: number; topupMaxPaise: number; walletMaxBalancePaise: number; pendingTimeoutMinutes: number };
  visits: { photoRequired: boolean; passengerCountRequired: boolean; overstayMinutes: number };
  closing: { denominations: number[] };
  customers: { requireName: boolean; requirePlace: boolean };
  /** Charge for parking: off for Carnival City (free parking); on brings back fees, pricing rules and Collect payment. */
  parking: { feesEnabled: boolean };
  /** The wallet at shops the venue does not run itself (off by default: closed-loop wallet). */
  /** pointsAtAllShops: the owner's points system — every active shop takes points, whatever its own wallet switch says. */
  wallet: { acceptAtIndependentShops: boolean; pointsAtAllShops?: boolean };
  /** Turf bookings wait for approval (bookings.approve) when on; the super admin sets it on the turf rules page. */
  activities?: { turfApproval: boolean };
  /** Points fraud guards and alert levels: sent to the super admin only (left out for every other login). */
  security?: {
    maxAdjustmentPaise: number;
    alertTopupAbovePaise: number;
    alertDailyTopupsPerLoginPaise: number;
    deskPriceFloorPct: number;
    alertDiscountAbovePct: number;
  };
  /** The printer for every shop (the venue default): changed only by the super admin, the server counts its version. */
  printing?: VenuePrinterDefaults;
}

export interface VehicleType {
  code: string;
  name: string;
  sort: number;
  is_active: boolean;
}

export interface OutletRow {
  id: string;
  code: string;
  name: string;
  address: string | null;
  phone: string | null;
  upi_vpa: string | null;
  upi_payee_name: string | null;
  qr_version: number;
  is_active: boolean;
  created_at: string;
}

export interface AccountGroup {
  id: string;
  parent_id: string | null;
  name: string;
  type: 'income' | 'expense';
  code: string | null;
  is_archived: boolean;
  sort: number;
}

// Query keys are namespaced under 'admin' so they never collide with other screens' caches.
export const keys = {
  settings: ['admin', 'settings'] as const,
  vehicleTypes: ['admin', 'vehicle-types'] as const,
  outlets: ['admin', 'outlets'] as const,
  groups: (includeArchived: boolean) => ['admin', 'account-groups', includeArchived] as const,
};

export function useSettings() {
  return useQuery({ queryKey: keys.settings, queryFn: () => api<AppSettings>('/settings'), staleTime: 60_000 });
}

export function useVehicleTypes() {
  return useQuery({
    queryKey: keys.vehicleTypes,
    queryFn: async () => (await api<{ items: VehicleType[] }>('/vehicle-types')).items,
    staleTime: 300_000,
  });
}

export function useOutletList(enabled = true) {
  return useQuery({ queryKey: keys.outlets, queryFn: async () => (await api<{ items: OutletRow[] }>('/outlets')).items, enabled, staleTime: 60_000 });
}

export function useAccountGroups(includeArchived = false) {
  return useQuery({
    queryKey: keys.groups(includeArchived),
    queryFn: async () => (await api<{ items: AccountGroup[] }>('/account-groups', { query: { includeArchived: includeArchived ? 1 : undefined } })).items,
    staleTime: 60_000,
  });
}

// ------------------------------------------------------------------ account group tree
export interface GroupNode {
  group: AccountGroup;
  depth: number;
  path: string;
  children: GroupNode[];
}

/** Builds the group hierarchy (the API returns a flat list ordered by type, sort, name). */
export function groupTree(groups: AccountGroup[]): GroupNode[] {
  const byParent = new Map<string | null, AccountGroup[]>();
  const ids = new Set(groups.map((g) => g.id));
  for (const g of groups) {
    const parent = g.parent_id && ids.has(g.parent_id) ? g.parent_id : null;
    byParent.set(parent, [...(byParent.get(parent) ?? []), g]);
  }
  const build = (parent: string | null, depth: number, prefix: string): GroupNode[] =>
    (byParent.get(parent) ?? [])
      .slice()
      .sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name))
      .map((g) => {
        const path = prefix ? `${prefix} › ${g.name}` : g.name;
        return { group: g, depth, path, children: build(g.id, depth + 1, path) };
      });
  return build(null, 0, '');
}

/** Depth-first flattening for <select> options. */
export function flattenTree(nodes: GroupNode[], keep: (g: AccountGroup) => boolean = () => true): GroupNode[] {
  const out: GroupNode[] = [];
  const walk = (ns: GroupNode[]) => {
    for (const n of ns) {
      if (!keep(n.group)) continue;
      out.push(n);
      walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

export const indent = (depth: number) => '   '.repeat(depth);

// ------------------------------------------------------------------ small controls
export function useDebounced<T>(value: T, ms = 350): T {
  const json = JSON.stringify(value ?? null);
  const [settled, setSettled] = useState(json);
  useEffect(() => {
    const t = setTimeout(() => setSettled(json), ms);
    return () => clearTimeout(t);
  }, [json, ms]);
  return useMemo(() => JSON.parse(settled) as T, [settled]);
}

/** Whole-number text input (avoids number-input quirks on mobile); empty means null. */
export function IntInput({
  value,
  onChange,
  ...rest
}: { value: number | null; onChange: (v: number | null) => void } & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'>) {
  return (
    <Input
      inputMode="numeric"
      className="tabular"
      value={value ?? ''}
      onChange={(e) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
        onChange(digits === '' ? null : Number(digits));
      }}
      {...rest}
    />
  );
}

/**
 * The kit's DateRange asks its inputs for `w-auto`, but Input's base `w-full` wins in the generated
 * CSS, leaving both date inputs at 100% width (overflow once the viewport is resized). Force auto here.
 */
export function DateRangeField(props: Parameters<typeof DateRange>[0]) {
  return (
    <div className="[&_input]:w-auto!">
      <DateRange {...props} />
    </div>
  );
}

const linkBtn = 'inline-flex h-8 items-center justify-center gap-2 rounded-lg bg-white px-3 text-sm font-medium text-slate-800 ring-1 ring-slate-300 hover:bg-slate-50';

/** Plain link styled as a secondary button; used for cookie-authenticated downloads. */
export function DownloadLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} download className={linkBtn}>
      {children}
    </a>
  );
}

export function OutletSelect({
  value,
  onChange,
  outlets,
  allLabel = 'All outlets',
  allowAll = true,
  label = 'Outlet',
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  outlets: { id: string; name: string; code?: string | null }[];
  allLabel?: string;
  allowAll?: boolean;
  label?: string;
  className?: string;
}) {
  // A long list (many shops) gets a find box: type part of a shop's name or code to narrow it, also on a phone.
  return (
    <SearchableSelect
      label={label}
      value={value}
      onChange={onChange}
      options={outlets}
      allLabel={allowAll ? allLabel : undefined}
      className={clsx('w-auto! min-w-40', className)}
    />
  );
}

/** A bordered list of checkboxes for picking several options; a long list (more than 12) gets a box to narrow it down. */
export function MultiCheck({
  options,
  value,
  onChange,
  emptyHint,
  disabled,
  searchPlaceholder = 'Type to narrow the list',
}: {
  options: { value: string; label: string; hint?: string }[];
  value: string[];
  onChange: (v: string[]) => void;
  emptyHint?: string;
  disabled?: boolean;
  /** Placeholder of the filter box shown on long lists, e.g. "Find a shop by name". */
  searchPlaceholder?: string;
}) {
  const [filter, setFilter] = useState('');
  if (!options.length) return <p className="text-sm text-slate-500">{emptyHint ?? 'Nothing to choose from'}</p>;
  const searchable = options.length > MULTI_CHECK_SEARCH_FROM;
  const shown = searchable ? filterByQuery(options, filter, (o) => [o.label, o.hint]) : options;
  const hiddenTicked = searchable && filter.trim() ? value.filter((v) => !shown.some((o) => o.value === v)).length : 0;
  return (
    <div className="flex flex-col gap-1.5">
      {searchable && (
        <Input
          type="search"
          aria-label={searchPlaceholder}
          placeholder={searchPlaceholder}
          value={filter}
          maxLength={100}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={(e) => {
            // Enter in the filter box must not submit the form around it.
            if (e.key === 'Enter') e.preventDefault();
            if (e.key === 'Escape' && filter) {
              e.preventDefault();
              setFilter('');
            }
          }}
        />
      )}
      <div className="max-h-44 overflow-y-auto rounded-lg border border-slate-200 p-2">
        {shown.length === 0 ? (
          <p className="flex flex-wrap items-center gap-2 px-1.5 py-1 text-sm text-slate-500">
            No results for “{filter.trim()}”.
            <button type="button" className="font-medium text-brand-700 hover:underline" onClick={() => setFilter('')}>
              Clear
            </button>
          </p>
        ) : (
          shown.map((o) => {
            const checked = value.includes(o.value);
            return (
              <label key={o.value} className={clsx('flex items-start gap-2 rounded px-1.5 py-1 text-sm', !disabled && 'hover:bg-slate-50')}>
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-slate-300"
                  checked={checked}
                  disabled={disabled}
                  onChange={(e) => onChange(e.target.checked ? [...value, o.value] : value.filter((v) => v !== o.value))}
                />
                <span>
                  {o.label}
                  {o.hint && <span className="block text-xs text-slate-500">{o.hint}</span>}
                </span>
              </label>
            );
          })
        )}
      </div>
      {searchable && (filter.trim() || value.length > 0) && (
        <p className="text-xs text-slate-500">
          {filter.trim() ? `Showing ${shown.length} of ${options.length}` : `${options.length} in the list`}
          {value.length > 0 && ` · ${value.length} ticked${hiddenTicked ? ` (${hiddenTicked} not shown)` : ''}`}
        </p>
      )}
    </div>
  );
}

/** Lists longer than this get a filter box in MultiCheck. */
export const MULTI_CHECK_SEARCH_FROM = 12;

/** Editable list of short tokens (payment modes, denominations). */
export function ChipsEditor({
  values,
  onChange,
  normalize,
  placeholder,
  format = (v) => v,
  label,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  normalize: (raw: string) => { value: string } | { error: string };
  placeholder: string;
  format?: (v: string) => string;
  label: string;
}) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const add = () => {
    if (!draft.trim()) return;
    const r = normalize(draft);
    if ('error' in r) return setError(r.error);
    if (values.includes(r.value)) return setError('Already in the list');
    onChange([...values, r.value]);
    setDraft('');
    setError(null);
  };
  return (
    <div>
      <ul aria-label={label} className="mb-2 flex flex-wrap gap-2">
        {values.map((v) => (
          <li key={v} className="inline-flex items-center gap-1 rounded-full bg-slate-100 py-1 pl-3 pr-1 text-sm text-slate-800">
            {format(v)}
            <button
              type="button"
              onClick={() => onChange(values.filter((x) => x !== v))}
              className="rounded-full px-1.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
              aria-label={`Remove ${format(v)}`}
            >
              ×
            </button>
          </li>
        ))}
        {!values.length && <li className="text-sm text-slate-500">None</li>}
      </ul>
      <div className="flex gap-2">
        <Input
          aria-label={`Add to ${label}`}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          className="max-w-56"
        />
        <Button variant="secondary" onClick={add}>
          Add
        </Button>
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

/**
 * Confirmation dialog for consequential actions. With `reason`, a written reason is required and
 * passed to onConfirm. Errors from the server are shown inside the dialog.
 */
export function ConfirmModal({
  open,
  onClose,
  title,
  children,
  confirmLabel = 'Confirm',
  danger,
  reason,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  reason?: { label: string; placeholder?: string };
  onConfirm: (reason: string) => Promise<unknown>;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    if (open) {
      setText('');
      setError(null);
    }
  }, [open]);
  const needsReason = !!reason && text.trim().length < 3;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            loading={busy}
            disabled={needsReason}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await onConfirm(text.trim());
                onClose();
              } catch (e) {
                setError(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-sm text-slate-700">
        {children}
        {reason && (
          <Field label={reason.label} hint="At least 3 characters. This is kept in the audit log.">
            {(id) => <Textarea id={id} value={text} placeholder={reason.placeholder} onChange={(e) => setText(e.target.value)} maxLength={300} />}
          </Field>
        )}
        <ErrorNote error={error} />
      </div>
    </Modal>
  );
}

/** Opens a private file (receipt) in a new tab via a short-lived signed URL. */
export async function openSignedFile(getUrl: () => Promise<string>, onError: (e: unknown) => void) {
  // Open synchronously so popup blockers allow it, then point it at the signed URL.
  const w = window.open('about:blank', '_blank');
  try {
    const url = await getUrl();
    if (w) {
      w.opener = null;
      w.location.href = url;
    } else {
      window.location.href = url;
    }
  } catch (e) {
    w?.close();
    onError(e);
  }
}

export const modeLabel = (m: string) => (m === 'upi' ? 'UPI' : titleCase(m));

/** Compact Indian currency for chart axes: ₹950, ₹12k, ₹3.4L, ₹1.2Cr. */
export function compactInr(paise: number) {
  const r = paise / 100;
  const abs = Math.abs(r);
  const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
  if (abs >= 1e7) return `₹${fmt(r / 1e7)}Cr`;
  if (abs >= 1e5) return `₹${fmt(r / 1e5)}L`;
  if (abs >= 1e3) return `₹${fmt(r / 1e3)}k`;
  return `₹${Math.round(r)}`;
}

export const pct = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—');
